import { useCallback, useEffect, useState } from 'react';
import { useChatApiClient } from '../context/ChatApiClientContext.jsx';
import { useChatNotifications } from '../context/ChatNotificationsContext.jsx';
import { listDrives } from '../services/drives.js';
import { listFoldersByTenant } from '../services/folders.js';
import { listDocumentsByTenant } from '../services/documents.js';

const FOLDER_LIST_META = {
  page: 1,
  page_size: 100,
  order_by: ['updated_at'],
  order_direction: ['DESC'],
  filters: {},
  cursor: null,
};

const unwrapListBody = (response) => {
  const d = response?.data ?? response;
  if (Array.isArray(d?.body)) return d.body;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.body?.data)) return d.body.data;
  if (Array.isArray(d?.items)) return d.items;
  return [];
};

/**
 * Loads vault list when the drive picker opens and exposes fetchers for `DrivePickerModal` (`@aimsos/ui`).
 * Mirrors ann-web-app `useDrivesList` + folder/document list payloads.
 *
 * @param {boolean} isOpen
 */
export function useChatDrivePicker(isOpen) {
  const apiClient = useChatApiClient();
  const toast = useChatNotifications();
  const [drives, setDrives] = useState([]);
  const [isLoadingDrives, setIsLoadingDrives] = useState(false);

  const fetchDrives = useCallback(async () => {
    if (!apiClient) return;
    setIsLoadingDrives(true);
    try {
      const resp = await listDrives(apiClient, {
        scope: 'MANAGE',
        meta: {
          page: 1,
          page_size: 100,
          order_by: 'created_at',
          order_direction: 'DESC',
          filters: {},
        },
      });
      const items = unwrapListBody(resp);
      const mapped = items.map((item) => ({
        id: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
        name: item.name || item.title || 'Untitled Drive',
      }));
      setDrives(mapped.filter((row) => row.id));
    } catch (err) {
      console.error('Failed to load vault list:', err);
      toast.error?.({
        message: err?.message ?? 'Failed to load drives.',
      });
      setDrives([]);
    } finally {
      setIsLoadingDrives(false);
    }
  }, [apiClient, toast]);

  useEffect(() => {
    if (isOpen && drives.length === 0) void fetchDrives();
  }, [isOpen, drives.length, fetchDrives]);

  const fetchDriveRoot = useCallback(
    async (vaultUuid) => {
      const [foldersRes, docsRes] = await Promise.all([
        listFoldersByTenant(apiClient, {
          vault_uuid: vaultUuid,
          parent_folder_uuid: null,
          ...FOLDER_LIST_META,
        }),
        listDocumentsByTenant(apiClient, {
          vault_uuid: vaultUuid,
          page: 1,
          page_size: 100,
        }),
      ]);
      return {
        folders: unwrapListBody(foldersRes),
        documents: unwrapListBody(docsRes),
      };
    },
    [apiClient],
  );

  const fetchFolderChildren = useCallback(
    async ({ folderUuid, vaultUuid }) => {
      const [foldersRes, docsRes] = await Promise.all([
        listFoldersByTenant(apiClient, {
          parent_folder_uuid: folderUuid,
          vault_uuid: vaultUuid,
          ...FOLDER_LIST_META,
        }),
        listDocumentsByTenant(apiClient, {
          vault_uuid: vaultUuid,
          folder_uuid: folderUuid,
          page: 1,
          page_size: 100,
        }),
      ]);
      return {
        folders: unwrapListBody(foldersRes),
        documents: unwrapListBody(docsRes),
      };
    },
    [apiClient],
  );

  return {
    drives,
    isLoadingDrives,
    fetchDriveRoot,
    fetchFolderChildren,
  };
}
